import { createContext } from 'react';
import type { Language, TranslationKey, PluralKey, Vars } from './i18n';

/* =============================================================================
 * The context object, on its own, so it keeps its identity
 * -----------------------------------------------------------------------------
 * Three lines in a file of their own, and the reason is worth the file.
 *
 * A React context is matched by object identity. `useContext(X)` finds a
 * provider only if that provider rendered the *same* X — not an equal one, the
 * same one. `createContext()` makes a new object every time the module holding
 * it is evaluated.
 *
 * In production that happens once and the question never arises. In development
 * Vite re-evaluates a module whenever it, or something it imports, changes. And
 * i18n.tsx imports the dictionaries:
 *
 *     src/core/i18n/ar.ts  →  i18n.tsx  →  createContext() runs again
 *
 * The tree that is already mounted keeps rendering the *old* <I18nProvider>,
 * handing out the old context. Every component below it now reads the new one,
 * which no provider has ever rendered, so useContext returns null and useI18n
 * throws:
 *
 *     useI18n must be used inside <I18nProvider>
 *       at useT
 *       at AttendancePage
 *
 * That is a real report, filed against /attendance from a dev build, after an
 * edit to the Arabic dictionary — a file that is edited constantly and has
 * nothing to do with any provider. The screen goes white and the message points
 * at a provider that is present and correct three levels up.
 *
 * Keeping the context here fixes it: nothing edits this file, so Vite never
 * re-evaluates it, so the object is the same object all day. Edit the
 * dictionaries, edit the provider, edit the hooks — the identity holds.
 *
 * -----------------------------------------------------------------------------
 * WHY THE TYPE IMPORT IS NOT A CYCLE
 * -----------------------------------------------------------------------------
 * i18n.tsx imports this file, and this file imports types from i18n.tsx. A type
 * import is erased before anything runs, so there is no cycle at runtime — the
 * emitted module imports React and nothing else.
 * ========================================================================== */

export interface I18nContextValue {
  language: Language;
  dir: 'ltr' | 'rtl';
  setLanguage: (language: Language) => void;
  t: (key: TranslationKey, vars?: Vars) => string;
  /** A sentence whose wording depends on a count. See `plural` in i18n.tsx. */
  plural: (base: PluralKey, count: number, vars?: Vars) => string;
}

export const I18nContext = createContext<I18nContextValue | null>(null);
